// Shared booking field definitions used by Bookings (admin) and NewBooking (booking_agent).

export const STATUS_OPTIONS = [
  { value: "submitted", label: "Submitted" },
  { value: "rejected", label: "Sent Back / Rejected" },
  { value: "pre_booking", label: "Pre-booking" },
  { value: "current", label: "Current / Out" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export const RENTAL_CATEGORY = [
  { value: "retail", label: "Retail" },
  { value: "insurance", label: "Insurance Replacement" },
];

export const CHAUFFEUR_SERVICE = [
  { value: "airport", label: "Airport Transfer" },
  { value: "hourly", label: "Hourly" },
  { value: "point_to_point", label: "Point to Point" },
  { value: "city_tour", label: "City Tour" },
  { value: "wedding", label: "Wedding / Event" },
  { value: "corporate", label: "Corporate" },
  { value: "other", label: "Other" },
];

// Client credentials — rendered as its own section at the top of the booking sheet.
// All plain text boxes the agent types into. Starred (required) fields block submit.
// (Insurance Coverage is a Yes/No bubble handled separately in BookingForm.)
export const CLIENT_CREDENTIAL_FIELDS = [
  { name: "customer_name", label: "Client Name", required: true },
  { name: "customer_phone", label: "Phone Number", required: true },
  { name: "customer_email", label: "Email" },
  { name: "client_age", label: "Age", required: true },
  { name: "credit_card", label: "Credit Card", required: true },
  { name: "deposit_amount", label: "Deposit Amount", required: true },
];

// Bubble (radio-style) options for the conditional insurance fields.
export const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
export const INSURED_TYPE_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
];
export const INSURANCE_POLICY_OPTIONS = [
  { value: "opcf", label: "OPCF" },
  { value: "liability", label: "Only Liability" },
];

// Returns an error message string if a required client-credential field is empty, else null.
export function validateClientCredentials(form) {
  for (const f of CLIENT_CREDENTIAL_FIELDS) {
    if (f.required && !String(form[f.name] ?? "").trim()) {
      return `${f.label} is required`;
    }
  }
  if (!form.insurance_coverage) {
    return "Insurance Coverage (Yes/No) is required";
  }
  return null;
}

// Full booking validation: rental requires client credentials + rate/category;
// chauffeur/tour only require Client Name + Salesperson + Total.
const affiliateIssue = (form) =>
  String(form.salesperson ?? "").trim().toLowerCase() === "affiliate" && !String(form.affiliate_name ?? "").trim()
    ? "Pick which affiliate brought this booking in"
    : null;

export function validateBooking(form, formType, { isNew = false } = {}) {
  if (formType === "internal") {
    // SALES-022: a hold — vehicle, dates, who and why. No client, no money.
    if (!String(form.internal_user ?? "").trim()) return "Enter who is using the car";
    if (!String(form.vehicle_id ?? "").trim()) return "Select a vehicle";
    if (!String(form.start_datetime ?? "").trim()) return "Pick a start date & time";
    if (!String(form.end_datetime ?? "").trim()) return "Pick an end date & time";
    if (form.end_datetime < form.start_datetime) return "End must be after the start";
    return null;
  }
  if (formType === "rental") {
    const credErr = validateClientCredentials(form);
    if (credErr) return credErr;
    if (!String(form.salesperson ?? "").trim()) return "Salesperson is required";
    if (affiliateIssue(form)) return affiliateIssue(form);
    if (!form.total || Number(form.total) <= 0) return "Total is required";
    if (!form.rental_rate || Number(form.rental_rate) <= 0) return "Rate is required";
    if (!String(form.booking_category ?? "").trim()) return "Category is required";
    return null;
  }
  if (!String(form.customer_name ?? "").trim()) return "Client Name is required";
  if (!String(form.salesperson ?? "").trim()) return "Salesperson is required";
  if (affiliateIssue(form)) return affiliateIssue(form);
  if (!form.total || Number(form.total) <= 0) return "Total is required";
  // Newer requirements apply to NEW bookings only. Editing an older chauffeur
  // or tour booking must not be blocked on a field that did not exist when it
  // was taken — you would be unable to fix a typo without a phone number.
  if (isNew) {
    if (!String(form.customer_phone ?? "").trim()) return "Client Phone is required";
    if (!String(form.start_location ?? "").trim()) return "Pickup Location is required";
    if (!String(form.end_location ?? "").trim()) return "Drop-off Location is required";
    // Required but zero is a real answer: "no deposit taken" must be sayable.
    if (isBlank(form.depo_sent)) return "Deposit Sent is required — enter 0 if none was taken";
    if (chargesHourly(form) && !(Number(form.hourly_rate) > 0) && !hasPackage(form, formType)) {
      return 'Hourly Rate is required — or set Pricing to "Whole job" and type the total';
    }
  }
  return null;
}

// Today's date as YYYY-MM-DD (local), used to auto-fill the booking "Date" field.
export const todayISODate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Salesperson dropdown options (plus an "Other…" choice that reveals a free-text box).
export const SALESPERSON_OPTIONS = [
  "Obaid", "Emal", "Ewad", "Moe", "Rayhan", "Daud", "Collin", "Marcus", "Leon",
  "Morgan", "Henry", "Calvin", "Ethan", "Runner", "Ved", "Sam", "Jesse", "Aidan",
  "Shayan", "Affiliate",
];

// Sales tax rate applied when "Add Tax" is toggled on a booking (Ontario HST).
export const TAX_RATE = 0.13;

// ---------------------------------------------------------------------------
// Pricing mode (chauffeur & tour)
// "hourly" — total is computed: Hours x Rate + standby + extras.
// "flat"   — one price for the whole job, typed straight into Total. Nothing is
//            added on top of it, standby included: what the operator types is
//            what the client pays.
// ---------------------------------------------------------------------------
export const PRICING_MODE = [
  { value: "hourly", label: "Charge hourly" },
  { value: "flat", label: "Charge for the whole job" },
];

export const chargesHourly = (form) => (form?.pricing_mode || "hourly") !== "flat";

const n = (v) => Number(v || 0);
const r2 = (x) => Math.round(x * 100) / 100;
export const isBlank = (v) => String(v ?? "").trim() === "";
const sumPrices = (rows) => (Array.isArray(rows) ? rows : []).reduce((s, r) => s + n(r?.price), 0);

// Standby is waiting time INSIDE the booked window, not extra time bolted on
// the end of it. Billing the same hour at both the hourly rate and the standby
// rate would charge the client twice, so standby hours come out of the billable
// service hours: an 8-hour job with 2 hours of standby is 6 + 2, never 8 + 2.
// Chauffeur only — tours have no standby.
export const billableServiceHours = (windowHours, form, formType) => {
  const h = Number(windowHours) || 0;
  if (formType !== "chauffeur") return h;
  return Math.max(0, Math.round((h - (Number(form?.standby_hours) || 0)) * 10) / 10);
};

// Standby is chauffeur-only, and only bills in hourly mode.
export const standbyCharge = (form, formType) =>
  formType === "chauffeur" && chargesHourly(form)
    ? r2(n(form.standby_hours) * n(form.standby_rate))
    : 0;

// A tour priced off a package: base + every chosen itinerary option + chosen
// package add-ons. Reads the snapshot on the booking, never the live package.
export const packageCharge = (form) =>
  r2(n(form.package_base) + sumPrices(form.package_selections) + sumPrices(form.package_addons));

// Off-package client requests. Always billable, package or not.
export const customAddonsCharge = (form) => r2(sumPrices(form.custom_addons));

export const hasPackage = (form, formType) => formType === "tour" && !!String(form?.package_id || "").trim();

// Remaining balance is always Total minus the deposit — never typed by hand.
export const remainingBalance = (form) => r2(n(form.total) - n(form.depo_sent));

// Booking total as shown everywhere — includes HST when the tax toggle is on.
export const bookingTotal = (b) => {
  const t = Number(b.total || b.amount) || 0;
  return b.tax_applied ? Math.round(t * (1 + TAX_RATE) * 100) / 100 : t;
};

// SALES-023: inclusive calendar days between two datetimes — (end date −
// start date) + 1, minimum 1. Same-day is 1; Sep 1 → Sep 8 is 8. Date-only and
// UTC so time-of-day and DST never shift the count. The rule the business bills
// by, already used by the insurance module.
const _dateMs = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
};
export const inclusiveDays = (start, end) => {
  const s = _dateMs(start), e = _dateMs(end);
  if (s == null || e == null) return 1;
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
};

// SALES-029: the "24-hour" day count — how reps describe a day. Whole 24-hour
// periods from pickup to return, any part-period counting as one; exactly 24h
// is 1, a minute over is 2, minimum 1. Wall-clock (naive), mirroring the
// backend `hour_periods`. The default suggestion for a rental's days. Points
// never use this — they measure calendar days.
export const hourPeriods = (start, end) => {
  const a = Date.parse(start), b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
  const hours = (b - a) / 3600000;
  if (!(hours > 0)) return 1;
  return Math.max(1, Math.ceil(hours / 24 - 1e-9));
};

// SALES-027: calendar days an extension ADDS = date(new) − date(prev), date-only
// and UTC through the same `_dateMs` as `inclusiveDays`, so the time of day never
// shifts it. No +1 — this is days added, not an inclusive span: Sep 5→Sep 8 = 3,
// same day = 0, Sep 30→Oct 2 = 2. `null` when either date is unusable. The one
// frontend definition of extension days — the dialog preview and the history both
// use it, and old records display through it from their two stored dates.
export const extensionDays = (prev, next) => {
  const a = _dateMs(prev), b = _dateMs(next);
  if (a == null || b == null) return null;
  return Math.round((b - a) / 86400000);
};

// Live "charges" total used to auto-populate the Total field.
// Returns null when the operator owns the number (whole-job pricing) — both
// auto-total effects test `auto > 0`, so null makes them stand down.
export const computeChargesTotal = (form, formType) => {
  if (formType === "rental") {
    // SALES-029: price by whatever days the booking carries, half days included
    // (no rounding 0.5 up). No rental_days ⇒ 1, so pre-day-pricing bookings stay
    // byte-identical. Overnight fee is a per-booking rental add-on.
    const days = Number(form.rental_days) || 1;
    return r2(days * n(form.rental_rate) + n(form.delivery) + n(form.other_charges) + n(form.overnight_fee));
  }
  if (!chargesHourly(form)) return null;
  // A package replaces hours x rate on a tour; the package IS the price of the day.
  const service = hasPackage(form, formType) ? 0 : n(form.service_hours) * n(form.hourly_rate);
  const pkg = hasPackage(form, formType) ? packageCharge(form) : 0;
  return r2(service + pkg + standbyCharge(form, formType)
    + customAddonsCharge(form) + n(form.delivery) + n(form.other_charges));
};

// Field definitions for each booking type
export const RENTAL_FIELDS = [
  { name: "booking_date", label: "Date", type: "date", required: true },
  { name: "salesperson", label: "Salesperson", type: "salesperson-select", required: true },
  { name: "start_datetime", label: "Start of Rental Period", type: "datetime", required: true },
  { name: "end_datetime", label: "End of Rental Period", type: "datetime", required: true },
  { name: "vehicle_id", label: "Vehicle", type: "vehicle-select", service: "rental", required: true },
  { name: "vehicle_plate", label: "License Plate", readOnly: true },
  { name: "rental_days", label: "Days", type: "number" },
  { name: "rental_rate", label: "Daily rate", type: "number", required: true },
  { name: "delivery", label: "Delivery Charge", type: "number" },
  { name: "other_charges", label: "Other Charges", type: "number" },
  { name: "overnight_fee", label: "Overnight Fee", type: "number" },
  { name: "total", label: "Total", type: "number", required: true },
  { name: "gas_filled", label: "Gas Filled" },
  { name: "start_km", label: "Start KM", type: "number" },
  { name: "end_km", label: "End KM", type: "number" },
  { name: "booking_category", label: "Category", type: "select", options: RENTAL_CATEGORY, default: "retail", required: true, span: 2 },
  { name: "notes", label: "Notes", type: "textarea", span: 2 },
];

export const CHAUFFEUR_FIELDS = [
  { name: "booking_date", label: "Date", type: "date", required: true },
  { name: "salesperson", label: "Salesperson", type: "salesperson-select", required: true },
  { name: "customer_name", label: "Client Name", required: true },
  { name: "customer_email", label: "Client Email" },
  { name: "customer_phone", label: "Client Phone", required: true },
  { name: "service_type_detail", label: "Service Type", type: "select", options: CHAUFFEUR_SERVICE },
  { name: "vehicle_id", label: "Vehicle", type: "vehicle-select", service: "chauffeur", required: true },
  { name: "vehicle_plate", label: "Vehicle Plate", readOnly: true },
  { name: "start_datetime", label: "Start Time", type: "datetime", required: true },
  { name: "end_datetime", label: "End Time", type: "datetime", required: true },
  { name: "start_location", label: "Pickup Location", required: true },
  { name: "end_location", label: "Drop-off Location", required: true },
  { name: "in_between_stops", label: "In-between Stops", type: "textarea", span: 2 },
  { name: "driver_id", label: "Driver", type: "chauffeur-select" },
  { name: "pricing_mode", label: "Pricing", type: "select", options: PRICING_MODE, default: "hourly", required: true },
  { name: "service_hours", label: "Service Hours", type: "number" },
  { name: "hourly_rate", label: "Hourly Rate", type: "number", required: true, showIf: chargesHourly },
  { name: "standby_hours", label: "Standby Hours", type: "number" },
  { name: "standby_rate", label: "Standby Rate (per hour)", type: "number" },
  { name: "delivery", label: "Delivery Charge", type: "number" },
  { name: "other_charges", label: "Other Charges", type: "number" },
  { name: "total", label: "Total", type: "number", required: true },
  { name: "driver_cost", label: "Driver Cost", type: "number", adminOnly: true },
  { name: "gas_cost", label: "Gas Cost", type: "number", adminOnly: true },
  { name: "other_costs", label: "Other Costs", type: "number", adminOnly: true },
  { name: "owner_base_rate", label: "Owner Base Rate (3rd-party base-rate cars)", type: "number", adminOnly: true },
  { name: "owner_drove", label: "Owner drove their car (driver cost paid to owner — % split)", type: "checkbox", default: false, adminOnly: true },

  { name: "depo_sent", label: "Deposit Sent", type: "number", required: true },
  { name: "remaining_balance", label: "Remaining Balance (Total − Deposit)", type: "number", readOnly: true },
  { name: "notes", label: "Notes", type: "textarea", span: 2 },
];

// SALES-022: an internal hold asks for nothing but the vehicle, the dates, who
// is using the car and why. No client, pricing, insurance, salesperson or lead.
export const INTERNAL_FIELDS = [
  { name: "vehicle_id", label: "Vehicle", type: "vehicle-select", required: true },
  { name: "vehicle_plate", label: "License Plate", readOnly: true },
  { name: "start_datetime", label: "Start", type: "datetime", required: true },
  { name: "end_datetime", label: "End", type: "datetime", required: true },
  { name: "internal_user", label: "Used by", required: true, span: 2 },
  { name: "internal_reason", label: "Reason", type: "textarea", span: 2 },
];
// No standby: that is a chauffeur concept.
export const TOUR_FIELDS = [
  { name: "booking_date", label: "Date", type: "date", required: true },
  { name: "salesperson", label: "Salesperson", type: "salesperson-select", required: true },
  { name: "customer_name", label: "Client Name", required: true },
  { name: "customer_email", label: "Client Email" },
  { name: "customer_phone", label: "Client Phone", required: true },
  { name: "tour_name", label: "Tour / Package Name", span: 2 },
  { name: "package_id", label: "Package", type: "tour-package", span: 2 },
  { name: "custom_addons", label: "Custom Add-ons (not part of the package)", type: "custom-addons", span: 2 },
  { name: "vehicle_id", label: "Vehicle", type: "vehicle-select", service: "chauffeur", required: true },
  { name: "vehicle_plate", label: "Vehicle Plate", readOnly: true },
  { name: "start_datetime", label: "Start Time", type: "datetime", required: true },
  { name: "end_datetime", label: "End Time", type: "datetime", required: true },
  { name: "passenger_count", label: "Passengers", type: "number" },
  { name: "start_location", label: "Pickup Location", required: true },
  { name: "end_location", label: "Drop-off Location", required: true },
  { name: "in_between_stops", label: "Itinerary / Stops", type: "textarea", span: 2 },
  { name: "driver_id", label: "Driver", type: "chauffeur-select" },
  { name: "pricing_mode", label: "Pricing", type: "select", options: PRICING_MODE, default: "hourly", required: true },
  { name: "service_hours", label: "Service Hours", type: "number" },
  { name: "hourly_rate", label: "Hourly Rate", type: "number", required: true, showIf: chargesHourly },
  { name: "delivery", label: "Delivery Charge", type: "number" },
  { name: "other_charges", label: "Other Charges", type: "number" },
  { name: "total", label: "Total", type: "number", required: true },
  { name: "depo_sent", label: "Deposit Sent", type: "number", required: true },
  { name: "remaining_balance", label: "Remaining Balance (Total − Deposit)", type: "number", readOnly: true },
  { name: "notes", label: "Notes", type: "textarea", span: 2 },
];
